import React, { useState } from 'react';
import { apiFetch, apiPostForm } from '../lib/api-client';
import { ChannelSelector } from './ChannelSelector';
import { CategorySelector } from './CategorySelector';
import type { ParsedXtreamChannel } from '@iptv/shared';

interface PlaylistImportResponse {
  id: string;
  name: string;
  type: string;
  channel_count: number;
}

interface Category {
  id: string | number;
  name: string;
}

export function AddPlaylist() {
  const [inputType, setInputType] = useState<'url' | 'file' | 'xtream'>('url');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<PlaylistImportResponse | null>(null);
  const [xtreamChannels, setXtreamChannels] = useState<ParsedXtreamChannel[]>([]);
  const [xtreamCategories, setXtreamCategories] = useState<Category[]>([]);
  const [filteredChannels, setFilteredChannels] = useState<ParsedXtreamChannel[]>([]);
  const [step, setStep] = useState<'form' | 'category' | 'channel'>('form');

  const handleCategorySelection = (selectedCategories: Category[], filteredChannels: ParsedXtreamChannel[]) => {
    setFilteredChannels(filteredChannels);
    setStep('channel');
  };

  const handleChannelSelection = async (selectedChannels: ParsedXtreamChannel[]) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiFetch<PlaylistImportResponse>('/playlists', {
        method: 'POST',
        body: JSON.stringify({
          type: 'xtream',
          name: name || 'Xtream Playlist',
          server_url: serverUrl,
          username,
          password,
          selected_channels: selectedChannels,
        }),
      });

      setSuccess(result);
      setStep('form');
      setXtreamChannels([]);
      setXtreamCategories([]);
      setFilteredChannels([]);
      setServerUrl('');
      setUsername('');
      setPassword('');
      setName('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (inputType === 'url') {
        if (!url) {
          setError('Please enter a URL');
          setLoading(false);
          return;
        }

        const result = await apiFetch<PlaylistImportResponse>('/playlists', {
          method: 'POST',
          body: JSON.stringify({
            type: 'm3u',
            name: name || 'Imported Playlist',
            source: { url },
          }),
        });

        setSuccess(result);
        setUrl('');
        setName('');
      } else if (inputType === 'file') {
        if (!file) {
          setError('Please select a file');
          setLoading(false);
          return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', name || 'Imported Playlist');

        const result = await apiPostForm<PlaylistImportResponse>(
          '/playlists',
          formData
        );

        setSuccess(result);
        setFile(null);
        setName('');
      } else if (inputType === 'xtream') {
        if (!serverUrl || !username || !password) {
          setError('Please fill in all Xtream Codes fields');
          setLoading(false);
          return;
        }

        // Parse channels (no DB insert yet)
        const parseResult = await apiFetch<{
          channels: ParsedXtreamChannel[];
          categories: Category[];
        }>(
          '/playlists/parse',
          {
            method: 'POST',
            body: JSON.stringify({
              type: 'xtream',
              server_url: serverUrl,
              username,
              password,
            }),
          }
        );

        setXtreamChannels(parseResult.channels);
        setXtreamCategories(parseResult.categories);
        setStep('category');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (step === 'category') {
    return (
      <CategorySelector
        categories={xtreamCategories}
        channels={xtreamChannels}
        onNext={handleCategorySelection}
        onBack={() => {
          setStep('form');
          setXtreamChannels([]);
          setXtreamCategories([]);
        }}
      />
    );
  }

  if (step === 'channel') {
    return (
      <ChannelSelector
        channels={filteredChannels}
        onSubmit={handleChannelSelection}
        onBack={() => {
          setStep('category');
          setFilteredChannels([]);
        }}
      />
    );
  }

  return (
    <div className="add-playlist">
      <h1>Add Playlist</h1>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>
            <input
              type="radio"
              value="url"
              checked={inputType === 'url'}
              onChange={(e) => setInputType(e.target.value as 'url' | 'file' | 'xtream')}
            />
            Import from URL
          </label>
          <label>
            <input
              type="radio"
              value="file"
              checked={inputType === 'file'}
              onChange={(e) => setInputType(e.target.value as 'url' | 'file' | 'xtream')}
            />
            Upload File
          </label>
          <label>
            <input
              type="radio"
              value="xtream"
              checked={inputType === 'xtream'}
              onChange={(e) => setInputType(e.target.value as 'url' | 'file' | 'xtream')}
            />
            Xtream Codes Account
          </label>
        </div>

        <div className="form-group">
          <label>
            Playlist Name (optional):
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Playlist"
            />
          </label>
        </div>

        {inputType === 'url' && (
          <div className="form-group">
            <label>
              M3U/M3U8 URL:
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/playlist.m3u"
                required
              />
            </label>
          </div>
        )}

        {inputType === 'file' && (
          <div className="form-group">
            <label>
              M3U/M3U8 File:
              <input
                type="file"
                accept=".m3u,.m3u8,text/plain"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                required
              />
            </label>
          </div>
        )}

        {inputType === 'xtream' && (
          <>
            <div className="form-group">
              <label>
                Server URL:
                <input
                  type="url"
                  value={serverUrl}
                  onChange={(e) => setServerUrl(e.target.value)}
                  placeholder="https://xtream.example.com"
                  required
                />
              </label>
            </div>
            <div className="form-group">
              <label>
                Username:
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="username"
                  required
                />
              </label>
            </div>
            <div className="form-group">
              <label>
                Password:
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="password"
                  required
                />
              </label>
            </div>
          </>
        )}

        <button type="submit" disabled={loading}>
          {loading ? 'Importing...' : 'Import Playlist'}
        </button>
      </form>

      {error && <div className="error">{error}</div>}

      {success && (
        <div className="success">
          <h2>Success!</h2>
          <p>Playlist "{success.name}" imported with {success.channel_count} channels</p>
          <a href={`/playlists/${success.id}`}>View Playlist</a>
        </div>
      )}
    </div>
  );
}
